import { ChartMeta, ReportRecordData, ReportWidget } from '@/services/api/resources/analytics-builder/model';
import { useDetailedViewStore } from '@/stores/analytics/detailedView';
import { SetupContext } from 'vue';
import { useDetailedView } from '@/ui/concerns/analytics/useDetailedView';
import Highcharts, {
  Chart,
  Point,
  SVGElement,
  SeriesColumnOptions,
  SeriesSplineOptions,
  SeriesPieOptions,
} from 'highcharts';
import { useReportInjection } from '@/ui/concerns/analytics/useReportInjection';

export const GUEST_EXTRA_HEIGHT = 35;

export type Options = Pick<Highcharts.Options, 'chart' | 'plotOptions' | 'series' | 'xAxis' | 'yAxis'>;
export type PointWithMeta = (Point & { meta: ChartMeta });
export type ChartType = 'column' | 'spline' | 'pie';
const MIN_PIXELS_PER_SERIES_POINT = 20;
const MIN_PLOT_AREA_SIZE = 638;

export const useCharts = (
  context: SetupContext,
  reportId: number | undefined,
  report: ReportRecordData | null,
  widget: ReportWidget,
) => {
  const store = useDetailedViewStore();
  const { isGuest } = useReportInjection();
  const { getDetailedViewType } = useDetailedView();

  const onElementClick = (resourceId: number | string, head: string) => {
    if (isGuest) {
      return;
    }

    store.setDetailedView({
      reportId,
      report,
      resourceId,
      widgetKey: widget.key,
      detailedViewType: getDetailedViewType(widget.metric!),
      options: { head },
    });
    context.emit('detailed-view-open');
  };

  const formatLabels = (value: number, isPercentage: boolean) => `${value}${isPercentage ? '%' : ''}`;

  const getPlotAreaMinWidth = (
    series: Array<SeriesColumnOptions | SeriesSplineOptions | SeriesPieOptions>,
  ): number => {
    const size = (series[0].data?.length ?? 0) * MIN_PIXELS_PER_SERIES_POINT;

    return size < MIN_PLOT_AREA_SIZE ? MIN_PLOT_AREA_SIZE : size;
  };

  let exitButton: SVGElement;

  const getDefaultChartConfig = (
    labels: Array<string>,
    series: Array<SeriesColumnOptions | SeriesSplineOptions | SeriesPieOptions>,
    formatXLabels: Function,
    formatYLabels: Function,
    type: ChartType,
    height: number,
    isPercentage = false,
    isScrollable = false,
    isZoomable = false,
    elementClickCallback: typeof onElementClick = onElementClick,
  ): Options => ({
    chart: {
      type,
      height,
      ...(isScrollable
        ? {
          scrollablePlotArea: {
            minWidth: getPlotAreaMinWidth(series),
          },
        }
        : {}),
      ...(isZoomable
        ? {
          zooming: {
            type: 'x',
            resetButton: {
              position: {
                x: -50,
                y: 12,
              },
            },
          },
          panning: {
            enabled: true,
          },
          panKey: 'shift',
        }
        : {}),
      events: {
        fullscreenClose() {
          exitButton?.destroy();
        },
        fullscreenOpen(event: Chart) {
          const { target } = (event as unknown as Event);

          if (!target) {
            return;
          }

          const chart = target as unknown as Chart;

          exitButton = chart.renderer
            ?.button('×', 0, 0, () => {
              chart.fullscreen?.close();
            })
            .align({ align: 'right', x: -50, verticalAlign: 'top', y: 10 })
            .attr({ zIndex: 10 })
            .addClass('fullscreen-exit')
            .add();
        },
        click(e: Highcharts.PointerEventObject) {
          const point = this.series[0].searchPoint(e, true) as PointWithMeta|null;

          if (!point) {
            return;
          }

          const { id, head } = point.meta;
          elementClickCallback(id, head);

          if (this.fullscreen?.isOpen) {
            this.fullscreen?.close();
          }
        },
      },
    },
    plotOptions: {
      series: {
        showInLegend: false,
        marker: {
          enabled: false,
        },
        point: {
          events: {
            click() {
              const { id, head } = (this as PointWithMeta).meta;
              elementClickCallback(id, head);

              if (this.series.chart.fullscreen?.isOpen) {
                this.series.chart.fullscreen?.close();
              }
            },
          },
        },
        cursor: 'pointer',
      },
      pie: {
        dataLabels: {
          style: {
            fontFamily: 'Barlow, Helvetica, Arial, sans-serif',
          },
        },
      },
    },
    xAxis: {
      categories: labels,
      labels: {
        y: 15,
        style: {
          fontSize: '12px',
          fontFamily: 'Barlow, Helvetica, Arial, sans-serif',
        },
        overflow: 'justify',
        formatter() {
          return String(formatXLabels(this.value as number));
        },
      },
      crosshair: {
        width: 1,
        dashStyle: 'Dash',
        color: '#4a4a4a',
      },
    },
    yAxis: {
      title: {
        text: '',
      },
      labels: {
        formatter() {
          return String(formatYLabels(this.value as number));
        },
      },
      max: isPercentage ? 100 : undefined,
    },
    series,
  });

  return {
    onElementClick,
    formatLabels,
    getDefaultChartConfig,
  };
};
